// auth.service.ts
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private readonly EXPIRATION_MS = 6 * 60 * 60 * 1000;
  //private readonly EXPIRATION_MS = 30 * 1000; // 30 segundos para testing
  private readonly INACTIVITY_MS = 15 * 60 * 1000; // 15 minutos de inactividad
  //private readonly INACTIVITY_MS = 30 * 1000; // 30 segundos de inactividad
  private readonly CHECK_INTERVAL_MS = 60 * 1000; // Verifica cada 1 minuto
  private currentUser: any = null;
  private expirationTimer: ReturnType<typeof setInterval> | null = null;
  private lastActivity: number = Date.now();
  private readonly activityEvents = ['click', 'keydown', 'mousemove'];
  private activityListener = () => this.updateLastActivity();

  constructor(private router: Router) {}

  /**
   * Decodifica el token base64, valida expiración y almacena el usuario en memoria.
   * @param token - Token base64 con formato: loginName|tipoDoc|nroDoc|timestamp
   * @returns true si el token es válido y no expiró, false en caso contrario
   */
  validateAndStoreToken(token: string): boolean {
    try {
      const decoded = atob(token);
      const [loginName, tipoDoc, nroDoc, timestamp] = decoded.split('|');
      
      const tokenTime = parseInt(timestamp);
      if (this.isTokenExpired(tokenTime)) {
        return false;
      }
      
      this.currentUser = { loginName, tipoDoc, nroDoc, tokenTime };
      this.startExpirationTimer();
      this.startActivityListeners();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Verifica si el token superó el tiempo máximo de sesión.
   * @param tokenTime - Timestamp en ms del momento de creación del token
   * @returns true si el token expiró o no existe
   */
  private isTokenExpired(tokenTime?: number): boolean {
    if (!tokenTime) return true;
    return Date.now() - tokenTime > this.EXPIRATION_MS;
  }

  /**
   * Verifica si el usuario superó el tiempo máximo de inactividad.
   * @returns true si el tiempo desde la última actividad supera INACTIVITY_MS
   */
  private isInactive(): boolean {
    return Date.now() - this.lastActivity > this.INACTIVITY_MS;
  }

  /** Actualiza el timestamp de última actividad del usuario */
  private updateLastActivity(): void {
    this.lastActivity = Date.now();
  }

  /** Registra listeners de actividad del usuario (click, tecla, mouse) */
  private startActivityListeners(): void {
    this.activityEvents.forEach(event =>
      document.addEventListener(event, this.activityListener, { passive: true })
    );
  }

  /** Remueve los listeners de actividad */
  private stopActivityListeners(): void {
    this.activityEvents.forEach(event =>
      document.removeEventListener(event, this.activityListener)
    );
  }

  /**
   * Inicia un intervalo que verifica periódicamente si la sesión expiró
   * por tiempo de token o por inactividad del usuario.
   */
  private startExpirationTimer(): void {
    this.stopExpirationTimer();
    this.expirationTimer = setInterval(() => {
      if (!this.currentUser) return;

      if (this.isTokenExpired(this.currentUser.tokenTime) || this.isInactive()) {
        this.clearSession();
        this.router.navigate(['/session-expired']);
      }
    }, this.CHECK_INTERVAL_MS);
  }

  /** Detiene el timer de verificación de expiración */
  private stopExpirationTimer(): void {
    if (this.expirationTimer) {
      clearInterval(this.expirationTimer);
      this.expirationTimer = null;
    }
  }

  /**
   * Retorna el usuario actual. Si la sesión expiró o está inactivo, limpia y retorna null.
   * @returns Objeto usuario o null si no hay sesión válida
   */
  getCurrentUser() {
    if (this.currentUser && (this.isTokenExpired(this.currentUser.tokenTime) || this.isInactive())) {
      this.clearSession();
      return null;
    }
    return this.currentUser;
  }

  /**
   * Retorna el loginName del usuario autenticado.
   * @returns loginName o 'USUARIO_DESCONOCIDO' si no hay sesión válida
   */
  getLoginName() {
    const user = this.getCurrentUser();
    return user?.loginName || 'USUARIO_DESCONOCIDO';
  }

  /** Limpia la sesión actual, detiene el timer y remueve listeners */
  clearSession() {
    this.currentUser = null;
    this.stopExpirationTimer();
    this.stopActivityListeners();
  }

  /** Cleanup al destruir el servicio */
  ngOnDestroy(): void {
    this.stopExpirationTimer();
    this.stopActivityListeners();
  }
}